import {ReactNode} from 'react';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import './landing-page.css';
import {
  ArrowRight,
  c,
  CheckMark,
  Container,
  Reveal,
  SiteFooter,
  SiteHeader,
} from './mrigl-shell';
import {MriglPacks} from './mrigl-packs';
import {TemplatesShowcase} from './templates-showcase';

export function LandingPage() {
  return (
    <div
      className="mrigl-landing min-h-screen"
      style={{background: c.bg, color: c.ink}}
    >
      <DefaultMetaTags />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-16 focus:top-16 focus:z-50 focus:rounded focus:bg-black focus:px-16 focus:py-8 focus:text-white"
      >
        Aller au contenu
      </a>
      <SiteHeader />
      <main id="main" className="mrigl-main">
        <Hero />
        <ProofStrip />
        <ExamplePreview />
        <HowItWorks />
        <TemplatesShowcase />
        <Included />
        <Pricing />
        <Audience />
        <Trust />
        <Faq />
        <FinalCta />
      </main>
      <SiteFooter />
      <StickyMobileCta />
    </div>
  );
}

function StickyMobileCta() {
  return (
    <div
      className="sticky-mobile-cta fixed bottom-0 left-0 right-0 z-40 border-t md:hidden"
      style={{
        borderColor: c.rule,
        background: 'rgba(255, 255, 255, 0.96)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        paddingTop: 12,
        paddingLeft: 16,
        paddingRight: 16,
        boxShadow: '0 -4px 16px rgba(10, 10, 10, 0.06)',
      }}
    >
      <div className="flex items-center gap-12">
        <div className="min-w-0 flex-1">
          <p
            className="m-0 truncate text-sm"
            style={{fontWeight: 700, color: c.ink, lineHeight: 1.2}}
          >
            Votre site en ligne, pour 8 TND par mois !
          </p>
          <p
            className="m-0 truncate text-xs"
            style={{color: c.inkMuted, lineHeight: 1.3}}
          >
            Tout compris, sans surprise.
          </p>
        </div>
        <Link
          to="/register"
          className="btn-lime inline-flex shrink-0 items-center justify-center gap-6 rounded px-18 py-12 text-sm font-semibold"
        >
          Créer
          <ArrowRight />
        </Link>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section
      className="preview-hero relative overflow-hidden pb-72 pt-56 md:pb-112 md:pt-88"
      aria-labelledby="hero-title"
    >
      <Container>
        <motion.div
          initial={{opacity: 0, y: 12}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.55, ease: [0.22, 0.61, 0.36, 1]}}
          className="mx-auto max-w-900 text-center"
        >
          <span className="preview-pill mb-24 inline-block">
            Simple. Clair. <strong>En ligne vite.</strong>
          </span>
          <h1
            id="hero-title"
            className="display preview-hero-title mb-24 text-5xl md:text-7xl"
          >
            Créez un site clair pour votre projet{' '}
            <span className="gradient-text">sans complexité.</span>
          </h1>

          <p
            className="hero-subtitle mx-auto mb-30 max-w-720 text-lg leading-7"
          >
            Mrigl.site aide les freelances, créateurs, artisans, cafés,
            cabinets et petits projets à publier une présence en ligne
            professionnelle avec un prix lisible.
          </p>

          <div className="mb-16 flex items-center justify-center">
            <Link
              to="/register"
              className="mrigl-gradient-button"
              aria-label="Créer mon site maintenant"
            >
              Créer mon site maintenant
              <ArrowRight />
            </Link>
          </div>

          <p className="hero-note">Sans engagement • SSL inclus • Support 24/7</p>

          <div className="trust-strip">
            <div className="avatars" aria-hidden="true">
              <div className="avatar">F</div>
              <div className="avatar">C</div>
              <div className="avatar">A</div>
              <div className="avatar">+</div>
            </div>
            <p>
              Fait pour les freelances, cafés, artisans, consultants et petites
              structures en Tunisie.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{opacity: 0, y: 28, scale: 0.985}}
          animate={{opacity: 1, y: 0, scale: 1}}
          transition={{duration: 0.7, delay: 0.14, ease: [0.22, 0.61, 0.36, 1]}}
          className="hero-preview-stage mockup-section mx-auto mt-44 max-w-1080 md:mt-64"
        >
          <div className="hero-mockup-drift">
            <BrowserPreview />
          </div>
        </motion.div>
      </Container>
    </section>
  );
}

const proofItems = [
  ['8 TND/mois', 'Un prix lisible, sans frais cachés.'],
  ['SSL inclus', "Votre site s'ouvre en connexion sécurisée."],
  ['1 après-midi', 'Un site prêt sans projet interminable.'],
  ['Tunisie', 'Support 24/7, facturation locale.'],
];

function ProofStrip() {
  return (
    <section
      className="border-t py-28 md:py-36"
      style={{borderColor: c.rule, background: c.ruleSoft}}
      aria-label="Points de confiance"
    >
      <Container>
        <Reveal>
          <div className="proof-grid grid gap-12 md:grid-cols-4">
            {proofItems.map(([value, label]) => (
              <div
                key={value}
                className="proof-card rounded border bg-white px-18 py-18"
                style={{borderColor: c.rule}}
              >
                <p
                  className="m-0 text-xl"
                  style={{fontWeight: 700, color: c.ink, lineHeight: 1.15}}
                >
                  {value}
                </p>
                <p
                  className="m-0 mt-6 text-sm"
                  style={{color: c.inkMuted, lineHeight: 1.45}}
                >
                  {label}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function ExamplePreview() {
  return (
    <section
      id="exemple"
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="example-title"
    >
      <Container className="grid items-center gap-48 md:grid-cols-[5fr_7fr] md:gap-64">
        <Reveal>
          <span className="eyebrow mb-20 inline-block">Exemple</span>
          <h2
            id="example-title"
            className="display mb-18 text-3xl md:text-5xl"
            style={{lineHeight: 1.08}}
          >
            Une page claire, crédible, prête à partager.
          </h2>
          <p
            className="mb-24 text-base leading-7"
            style={{color: c.inkMuted, maxWidth: 520}}
          >
            MRIGL est pensé pour les freelances, créateurs, artisans, cafés,
            cabinets et petits projets qui ont besoin d'une présence nette sans
            complexité.
          </p>
          <Link
            to="/register"
            className="btn-link inline-flex items-center gap-8 text-base font-semibold"
            style={{color: c.ink}}
          >
            Commencer avec un modèle
            <ArrowRight />
          </Link>
        </Reveal>

        <Reveal>
          <div
            className="rounded border bg-white p-12"
            style={{
              borderColor: c.rule,
              boxShadow:
                '0 28px 60px rgba(10, 10, 10, 0.08), 0 6px 18px rgba(10, 10, 10, 0.05)',
            }}
          >
            <BrowserPreview compact />
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

const steps = [
  {
    n: '01',
    title: 'Créez votre compte',
    body: 'Quelques minutes suffisent pour démarrer votre site.',
  },
  {
    n: '02',
    title: 'Choisissez un modèle',
    body: 'Portfolio, commerce local, restaurant, cabinet, projet ou marque personnelle.',
  },
  {
    n: '03',
    title: 'Modifiez et publiez',
    body: 'Changez textes, images et couleurs, puis mettez le site en ligne.',
  },
];

function HowItWorks() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="how-title"
    >
      <Container>
        <Reveal className="mx-auto mb-48 max-w-720 text-center md:mb-64">
          <span className="eyebrow mb-20 inline-block">Comment ça marche</span>
          <h2
            id="how-title"
            className="display text-3xl md:text-5xl"
            style={{lineHeight: 1.08, margin: '0 auto', maxWidth: '19ch'}}
          >
            En ligne sans technique compliquée.
          </h2>
        </Reveal>

        <Reveal>
          <ol
            className="m-0 grid list-none gap-20 p-0 md:gap-24"
            style={{
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
            }}
          >
            {steps.map(step => (
              <li
                key={step.n}
                className="conversion-card rounded border bg-white p-24 md:p-30"
                style={{borderColor: c.rule}}
              >
                <span
                  className="mb-18 inline-flex h-34 w-48 items-center justify-center rounded text-sm font-bold tabular-nums"
                  style={{background: c.lime, color: c.ink}}
                  aria-hidden="true"
                >
                  {step.n}
                </span>
                <h3
                  className="mb-8 text-lg"
                  style={{fontWeight: 700, color: c.ink, lineHeight: 1.3}}
                >
                  {step.title}
                </h3>
                <p
                  className="m-0 text-sm"
                  style={{color: c.inkMuted, lineHeight: 1.6}}
                >
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </Reveal>
      </Container>
    </section>
  );
}

const includedItems = [
  ['Hébergement', 'votre site est en ligne, sans configuration serveur.'],
  ["Lien d'accès inclus", 'une adresse claire à partager dès le départ.'],
  ['Certificat SSL', 'connexion sécurisée incluse par défaut.'],
  ['Domaine personnalisé', 'connectez votre propre domaine quand vous voulez.'],
  ['Modèles soignés', 'des bases propres pour mobile et desktop.'],
  ['Éditeur simple', 'textes, images et couleurs restent faciles à changer.'],
];

function Included() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule, background: c.ruleSoft}}
      aria-labelledby="included-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Inclus</span>
          <h2
            id="included-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{maxWidth: '14ch', lineHeight: 1.08}}
          >
            Tout ce qu'il faut, dans un seul prix.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '36ch', lineHeight: 1.6}}>
            Pas de modules obligatoires, pas de coûts cachés, pas de réglages
            techniques avant de publier.
          </p>
        </Reveal>

        <Reveal>
          <ul className="included-list m-0 list-none bg-white p-0">
            {includedItems.map(([lead, body]) => (
              <li
                key={lead}
                className="grid grid-cols-[1.6rem_1fr] items-start gap-16 px-20 py-22"
              >
                <span
                  className="mt-3 inline-flex h-20 w-20 items-center justify-center rounded-full"
                  style={{background: c.lime, color: c.ink}}
                  aria-hidden="true"
                >
                  <CheckMark />
                </span>
                <span style={{color: c.ink2, lineHeight: 1.55}}>
                  <b style={{fontWeight: 700, color: c.ink}}>{lead}</b> - {body}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </Container>
    </section>
  );
}

function Pricing() {
  return (
    <section
      id="creer"
      className="border-t py-96 md:py-144"
      style={{borderColor: c.rule, background: c.bg}}
      aria-labelledby="price-title"
    >
      <Container>
        <Reveal className="mx-auto mb-52 max-w-620 text-center">
          <span className="eyebrow mb-20 inline-block">Tarif</span>
          <h2
            id="price-title"
            className="display mb-18 text-3xl md:text-5xl"
            style={{color: c.ink, fontWeight: 700, lineHeight: 1.08}}
          >
            8 TND/mois. Pas de surprise.
          </h2>
          <p
            className="mx-auto max-w-560 text-lg leading-7"
            style={{color: c.inkMuted}}
          >
            Hébergement, SSL, lien d'accès et modèles inclus. Le prix reste
            lisible avant, pendant et après l'inscription.
          </p>
        </Reveal>

        <Reveal>
          <MriglPacks loader="landingPage" variant="full" />
        </Reveal>

        <Reveal className="mx-auto mt-40 max-w-520 text-center">
          <p className="text-sm" style={{color: c.inkMuted, lineHeight: 1.6}}>
            Vous voulez comparer les détails ?{' '}
            <Link
              to="/pricing"
              className="btn-link inline-flex items-center gap-6 font-semibold"
              style={{color: c.ink}}
            >
              Voir le tarif complet
              <ArrowRight />
            </Link>
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

const audienceFor = [
  ['Freelances et indépendants', 'portfolio, services, contact et crédibilité.'],
  ['Créateurs et marques personnelles', 'un lien central hors des réseaux sociaux.'],
  ['Artisans et petites entreprises', 'une vitrine simple à partager.'],
  ['Restaurants, cafés et salons', 'horaires, adresse, menu, contact.'],
  ['Cabinets et consultants', 'présentation claire et demande de contact.'],
  ['Projets en démarrage', 'une présence en ligne avant de tout construire.'],
];

const audienceNotFor = [
  'Boutique en ligne complète avec panier et paiement.',
  'Espace membre, tableau de bord ou comptes utilisateurs.',
  'Application web sur mesure ou intégrations complexes.',
  'Site multilingue avancé avec contenu lourd.',
];

function Audience() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="audience-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Pour qui</span>
          <h2
            id="audience-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{maxWidth: '18ch', lineHeight: 1.08}}
          >
            Fait pour les sites simples qui doivent inspirer confiance.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '38ch', lineHeight: 1.6}}>
            MRIGL va droit au but. Il aide les petits projets à être trouvés,
            compris et contactés.
          </p>
        </Reveal>

        <Reveal>
          <ul className="who-list m-0 list-none p-0">
            {audienceFor.map(([lead, body], i) => (
              <li
                key={lead}
                className="grid grid-cols-[2.4rem_1fr] items-baseline gap-x-10 py-22"
                style={{lineHeight: 1.55}}
              >
                <span
                  className="text-sm tabular-nums"
                  style={{color: c.inkSoft, fontWeight: 700}}
                  aria-hidden="true"
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span>
                  <b style={{fontWeight: 700, color: c.ink}}>{lead}</b>{' '}
                  <span style={{color: c.ink2}}>- {body}</span>
                </span>
              </li>
            ))}
          </ul>

          <aside
            className="mt-36 rounded border px-24 py-24"
            style={{
              borderColor: c.rule,
              background: c.ruleSoft,
              color: c.inkMuted,
              lineHeight: 1.55,
              fontSize: '0.95rem',
            }}
          >
            <p
              className="mb-12 text-sm"
              style={{color: c.ink, fontWeight: 700, margin: 0}}
            >
              Ce n'est pas le bon choix si vous avez besoin de :
            </p>
            <ul className="m-0 list-none space-y-8 p-0">
              {audienceNotFor.map(item => (
                <li key={item} className="flex items-start gap-10">
                  <span
                    className="mt-8 inline-block h-6 w-6 shrink-0 rounded-full"
                    style={{background: c.inkSoft}}
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </aside>
        </Reveal>
      </Container>
    </section>
  );
}

const trustSignals = [
  'Paiement en dinars tunisiens.',
  'Support 24/7, répondu par un humain.',
  'Hébergement et certificat SSL inclus.',
  'Aucun engagement long. Vous gardez le contrôle.',
];

function Trust() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule, background: c.bg}}
      aria-labelledby="trust-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Confiance</span>
          <h2
            id="trust-title"
            className="display mb-20 text-3xl md:text-5xl"
            style={{maxWidth: '15ch', lineHeight: 1.08}}
          >
            Construit en Tunisie, pour la Tunisie.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '38ch', lineHeight: 1.6}}>
            MRIGL parle aux petites structures locales : prix clair, support
            simple, et une expérience pensée pour publier sans se perdre.
          </p>
        </Reveal>

        <Reveal>
          <ul className="included-list m-0 list-none p-0">
            {trustSignals.map(item => (
              <li
                key={item}
                className="grid grid-cols-[1.4rem_1fr] items-start gap-16 py-22"
              >
                <span
                  className="mt-10 inline-block h-8 w-8 lime-mark"
                  aria-hidden="true"
                />
                <span style={{color: c.ink2, lineHeight: 1.55}}>{item}</span>
              </li>
            ))}
          </ul>

          <p
            className="mt-24 max-w-500 text-sm"
            style={{color: c.inkMuted, lineHeight: 1.6}}
          >
            Une question avant de commencer ?{' '}
            <Link
              to="/contact"
              className="btn-link inline-flex items-center gap-6 font-semibold"
              style={{color: c.ink}}
            >
              Écrivez-nous
              <ArrowRight />
            </Link>
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

const faqs = [
  [
    'Le prix change-t-il au renouvellement ?',
    'Non. Le positionnement reste simple : 8 TND par mois, avec hébergement, SSL et modèles inclus.',
  ],
  [
    'Combien de temps pour publier mon site ?',
    'Vous pouvez préparer une première version en une après-midi si vos textes et images sont prêts.',
  ],
  [
    'Puis-je connecter mon propre domaine ?',
    "Oui. Vous pouvez utiliser votre domaine ou garder le lien d'accès fourni par MRIGL.",
  ],
  [
    'Dois-je savoir coder ?',
    'Non. MRIGL est pensé pour modifier une page simplement, sans toucher au code.',
  ],
];

function Faq() {
  return (
    <section
      id="faq"
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="faq-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Questions</span>
          <h2
            id="faq-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{maxWidth: '14ch', lineHeight: 1.08}}
          >
            Les réponses avant de créer votre compte.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '36ch', lineHeight: 1.6}}>
            L'objectif est simple : vous savez ce que vous achetez avant de
            commencer.
          </p>
        </Reveal>

        <Reveal>
          <ul className="who-list m-0 list-none p-0">
            {faqs.map(([question, answer]) => (
              <li key={question} className="py-26">
                <p
                  className="mb-8 text-lg"
                  style={{fontWeight: 700, color: c.ink, margin: 0}}
                >
                  {question}
                </p>
                <p
                  className="text-base"
                  style={{
                    color: c.ink2,
                    margin: '8px 0 0 0',
                    lineHeight: 1.6,
                  }}
                >
                  {answer}
                </p>
              </li>
            ))}
          </ul>
        </Reveal>
      </Container>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      className="border-t py-96 md:py-144"
      style={{borderColor: c.rule, background: c.ruleSoft}}
      aria-labelledby="final-cta-title"
    >
      <Container>
        <Reveal className="mx-auto max-w-620 text-center">
          <span className="eyebrow mb-20 inline-block">Prêt ?</span>
          <h2
            id="final-cta-title"
            className="display mb-22 text-4xl md:text-6xl"
            style={{lineHeight: 1.06}}
          >
            Mettez votre site en ligne aujourd'hui.
          </h2>
          <p
            className="mx-auto mb-30 max-w-500 text-lg leading-7"
            style={{color: c.inkMuted}}
          >
            8 TND/mois. Tout inclus. Une page claire que vous pouvez modifier
            quand vous voulez.
          </p>

          <div className="mb-18 flex flex-wrap items-center justify-center gap-x-18 gap-y-14">
            <Link
              to="/register"
              className="btn-lime inline-flex items-center gap-8 rounded px-34 py-16 text-base font-semibold"
            >
              Créer mon site
              <ArrowRight />
            </Link>
            <Link
              to="/contact"
              className="btn-link inline-flex items-center gap-8 text-base font-semibold"
              style={{color: c.ink}}
            >
              Une question ?
              <ArrowRight />
            </Link>
          </div>

          <p className="text-xs" style={{color: c.inkSoft}}>
            Sans engagement. Support 24/7. SSL inclus.
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

function HeroMockup({compact = false}: {compact?: boolean}) {
  const height = compact ? 420 : 480;
  return (
    <svg
      viewBox={`0 0 720 ${height}`}
      xmlns="http://www.w3.org/2000/svg"
      className="block h-auto w-full"
      role="img"
      aria-label="Aperçu d'un site exemple créé avec MRIGL"
      style={{
        filter: compact
          ? undefined
          : 'drop-shadow(0 30px 50px rgba(10, 10, 10, .08)) drop-shadow(0 8px 16px rgba(10, 10, 10, .04))',
      }}
    >
      <rect
        x="6"
        y="6"
        width="708"
        height={height - 12}
        rx="10"
        fill="#FFFFFF"
        stroke="#E5E5E5"
      />
      <rect x="6" y="6" width="708" height="38" rx="10" fill="#FAFAFA" />
      <rect x="6" y="32" width="708" height="14" fill="#FAFAFA" />
      <line x1="6" y1="44" x2="714" y2="44" stroke="#E5E5E5" />
      <circle cx="28" cy="25" r="4.5" fill="#E5E5E5" />
      <circle cx="46" cy="25" r="4.5" fill="#E5E5E5" />
      <circle cx="64" cy="25" r="4.5" fill="#E5E5E5" />
      <rect x="238" y="15" width="244" height="18" rx="9" fill="#F2F2F2" />
      <text
        x="360"
        y="28"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="10"
        fill="#8C8C8C"
      >
        mrigl.site/rania
      </text>

      <text
        x="44"
        y="90"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="15"
        fontWeight="700"
        fill="#0A0A0A"
      >
        Rania Studio
      </text>
      <g
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="10"
        fill="#595959"
      >
        <text x="496" y="90">Services</text>
        <text x="566" y="90">Portfolio</text>
        <text x="646" y="90">Contact</text>
      </g>
      <line x1="44" y1="110" x2="676" y2="110" stroke="#F2F2F2" />

      <rect
        x="44"
        y="140"
        width="116"
        height="24"
        rx="12"
        fill={c.limeSoft}
      />
      <text
        x="102"
        y="156"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="9"
        fontWeight="700"
        fill="#0A0A0A"
      >
        Disponible à Tunis
      </text>
      <text
        x="44"
        y="208"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="42"
        fontWeight="700"
        fill="#0A0A0A"
      >
        Design clair pour
      </text>
      <text
        x="44"
        y="252"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="42"
        fontWeight="700"
        fill="#0A0A0A"
      >
        marques locales.
      </text>
      <rect x="44" y="266" width="245" height="7" fill={c.lime} />

      <text
        x="44"
        y="306"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="13"
        fill="#595959"
      >
        Identité visuelle, affiches et contenus pour cafés,
      </text>
      <text
        x="44"
        y="326"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="13"
        fill="#595959"
      >
        artisans et créateurs indépendants.
      </text>

      <rect x="44" y="354" width="132" height="34" rx="5" fill={c.lime} />
      <text
        x="110"
        y="376"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="11"
        fontWeight="700"
        fill="#0A0A0A"
      >
        Me contacter
      </text>
      <rect
        x="190"
        y="354"
        width="112"
        height="34"
        rx="5"
        fill="#FFFFFF"
        stroke="#E5E5E5"
      />
      <text
        x="246"
        y="376"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="11"
        fontWeight="700"
        fill="#0A0A0A"
      >
        Portfolio
      </text>

      <rect x="430" y="142" width="210" height="246" rx="8" fill="#0A0A0A" />
      <rect x="452" y="166" width="166" height="86" rx="5" fill={c.lime} />
      <rect x="452" y="268" width="74" height="96" rx="5" fill="#F2F2F2" />
      <rect x="544" y="268" width="74" height="96" rx="5" fill="#FFFFFF" />
      <text
        x="535"
        y="414"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="10"
        fill="#8C8C8C"
      >
        Exemple de page publiée avec MRIGL
      </text>
    </svg>
  );
}

function BrowserPreview({compact = false}: {compact?: boolean}) {
  return (
    <div className={`browser-frame ${compact ? 'browser-frame-compact' : ''}`}>
      <div className="browser-window">
        <div className="browser-top">
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
          <div className="url-bar">mrigl.site/rania</div>
        </div>
        <div className="site-preview">
          <div className="preview-left">
            <div className="preview-badge">Disponible à Tunis</div>
            <h2>Design clair pour marques locales.</h2>
            <p>
              Une page simple, crédible et prête à partager pour présenter vos
              services, portfolio, horaires, adresse et contact.
            </p>
            <div className="preview-buttons">
              <span className="preview-mini-btn">Me contacter</span>
              <span className="preview-mini-btn alt">Portfolio</span>
            </div>
          </div>
          <div className="preview-right">
            <div className="preview-card">
              <div className="preview-card-top">
                <span>Rania Studio</span>
                <span>MRIGL</span>
              </div>
              <div className="preview-image" />
              <div>
                <strong>Exemple de page publiée</strong>
                <p>
                  Services, portfolio, contact et informations essentielles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
